import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  QrCode,
  Download,
  Print,
  ContentCopy,
  Add,
  CheckCircle,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useOrganizations, useSites, useCreateDevice } from '../../hooks/useFleet';
import { SectionTabs } from '../../components/SectionTabs';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';
import type { Device } from '../../api/types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ProvisioningPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [heartbeatInterval, setHeartbeatInterval] = useState(60);
  const [createdDevice, setCreatedDevice] = useState<Device | null>(null);
  const [copied, setCopied] = useState(false);

  const qrRef = useRef<HTMLDivElement>(null);

  const { data: orgsData, isLoading: orgsLoading } = useOrganizations();
  const { data: sitesData, isLoading: sitesLoading } = useSites({
    organization_id: selectedOrg || undefined,
  });

  const createDevice = useCreateDevice();

  const organizations = orgsData?.data || [];
  const sites = sitesData?.data || [];

  const apiBaseUrl = window.location.hostname.includes('staging')
    ? 'https://staging-api.justplay.music'
    : 'https://api.justplay.music';

  const handleCreateDevice = async () => {
    if (!selectedOrg || !serialNumber) {
      return;
    }

    try {
      const device = await createDevice.mutateAsync({
        organization_id: selectedOrg,
        site_id: selectedSite || undefined,
        serial_number: serialNumber,
        name: deviceName || `Device ${serialNumber}`,
        model: deviceModel || undefined,
        heartbeat_interval: heartbeatInterval,
      });

      setCreatedDevice(device);
    } catch (error) {
      console.error('Failed to create device:', error);
    }
  };

  const getProvisioningUrl = () => {
    if (!createdDevice?.provisioning_token) return '';
    return `${apiBaseUrl}/api/fleet/provision/${createdDevice.provisioning_token}`;
  };

  const getGenericProvisioningUrl = () => {
    if (!selectedOrg) return '';
    return `${apiBaseUrl}/api/fleet/provision?org=${selectedOrg}`;
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngUrl = canvas.toDataURL('image/png');

      const link = document.createElement('a');
      link.download = `device-${createdDevice?.serial_number || 'qr'}.png`;
      link.href = pngUrl;
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);

    printWindow.document.write(`
      <html>
        <head>
          <title>Device Provisioning QR Code</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
            }
            .container {
              text-align: center;
              padding: 40px;
              border: 2px solid #333;
              border-radius: 8px;
            }
            h1 { margin: 0 0 10px 0; font-size: 24px; }
            p { margin: 5px 0; color: #666; }
            .serial { font-family: monospace; font-size: 18px; font-weight: bold; margin: 20px 0; }
            svg { margin: 20px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>JustPlay Device</h1>
            <p class="serial">${createdDevice?.serial_number || ''}</p>
            ${svgData}
            <p>Scan this QR code to provision the device</p>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `);
  };

  const resetForm = () => {
    setSerialNumber('');
    setDeviceName('');
    setDeviceModel('');
    setCreatedDevice(null);
  };

  return (
    <Box>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.provisioning}
      />

      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Create Device with Token" />
          <Tab label="Generic Organization QR" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {createdDevice ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle />
                  Device created successfully! Share this QR code with the device.
                </Box>
              </Alert>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {createdDevice.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', mb: 3 }}
                      >
                        {createdDevice.serial_number}
                      </Typography>

                      <Box
                        ref={qrRef}
                        sx={{
                          display: 'inline-block',
                          p: 2,
                          bgcolor: 'white',
                          borderRadius: 2,
                          mb: 3,
                        }}
                      >
                        <QRCodeSVG
                          value={getProvisioningUrl()}
                          size={200}
                          level="H"
                          includeMargin
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={handleDownloadQR}
                        >
                          Download
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Print />}
                          onClick={handlePrint}
                        >
                          Print
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                          onClick={() => handleCopyUrl(getProvisioningUrl())}
                        >
                          {copied ? 'Copied!' : 'Copy URL'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Device Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Token:</Typography>
                      <Typography sx={{ fontFamily: 'monospace' }}>
                        {createdDevice.provisioning_token}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Heartbeat:</Typography>
                      <Typography>{createdDevice.heartbeat_interval}s</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Status:</Typography>
                      <Typography sx={{ color: '#6366f1' }}>Provisioning</Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={resetForm}
                    fullWidth
                  >
                    Create Another Device
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Device Information
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Organization *</InputLabel>
                  <Select
                    value={selectedOrg}
                    label="Organization *"
                    onChange={(e) => {
                      setSelectedOrg(e.target.value);
                      setSelectedSite('');
                    }}
                    disabled={orgsLoading}
                  >
                    {organizations.map((org: any) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Site (Optional)</InputLabel>
                  <Select
                    value={selectedSite}
                    label="Site (Optional)"
                    onChange={(e) => setSelectedSite(e.target.value)}
                    disabled={!selectedOrg || sitesLoading}
                  >
                    <MenuItem value="">No Site</MenuItem>
                    {sites.map((site: any) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name} {site.city && `(${site.city})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Serial Number *"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value.toUpperCase())}
                  sx={{ mb: 2 }}
                  placeholder="e.g., JP-001234"
                />

                <TextField
                  fullWidth
                  label="Device Name"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="e.g., Lobby Player 1"
                />

                <TextField
                  fullWidth
                  label="Model"
                  value={deviceModel}
                  onChange={(e) => setDeviceModel(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="e.g., Raspberry Pi 4"
                />

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Heartbeat Interval</InputLabel>
                  <Select
                    value={heartbeatInterval}
                    label="Heartbeat Interval"
                    onChange={(e) => setHeartbeatInterval(Number(e.target.value))}
                  >
                    <MenuItem value={30}>30 seconds (Aggressive)</MenuItem>
                    <MenuItem value={60}>60 seconds (Standard)</MenuItem>
                    <MenuItem value={300}>5 minutes (Relaxed)</MenuItem>
                    <MenuItem value={600}>10 minutes (Economy)</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={createDevice.isPending ? <CircularProgress size={20} /> : <QrCode />}
                  onClick={handleCreateDevice}
                  disabled={!selectedOrg || !serialNumber || createDevice.isPending}
                  fullWidth
                >
                  {createDevice.isPending ? 'Creating...' : 'Create Device & Generate QR'}
                </Button>

                {createDevice.isError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {(createDevice.error as Error)?.message || 'Failed to create device'}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    How it works
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, m: 0 }}>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Fill in the device information and click "Create Device"
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        A unique provisioning QR code will be generated
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Print or download the QR code
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        On the device, scan the QR code during first boot
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        The device will automatically register and start reporting
                      </Typography>
                    </li>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Generic Organization QR Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Generate a QR code that can be used for any device. The device will
                prompt for serial number entry during provisioning.
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Organization *</InputLabel>
                <Select
                  value={selectedOrg}
                  label="Organization *"
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  disabled={orgsLoading}
                >
                  {organizations.map((org: any) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedOrg && (
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {organizations.find((o: any) => o.id === selectedOrg)?.name}
                    </Typography>

                    <Box
                      sx={{
                        display: 'inline-block',
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 2,
                        mb: 3,
                      }}
                    >
                      <QRCodeSVG
                        value={getGenericProvisioningUrl()}
                        size={200}
                        level="H"
                        includeMargin
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                        onClick={() => handleCopyUrl(getGenericProvisioningUrl())}
                      >
                        {copied ? 'Copied!' : 'Copy URL'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Generic QR Code Usage
                </Typography>
                <Box component="ol" sx={{ pl: 2, m: 0 }}>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Print multiple copies of the generic QR code
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Attach a QR code to each device
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      During provisioning, the device scans the QR
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Device prompts for serial number entry
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Device registers with the organization automatically
                    </Typography>
                  </li>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Alert severity="info">
                  Generic QR codes are reusable and don't expire. They're ideal for
                  bulk device deployment.
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ProvisioningPage;
